/**
 * สถานะการเข้าใช้งาน — ผู้ปกครองหรือเด็ก
 *
 * ⚠️ access token เก็บใน **หน่วยความจำเท่านั้น** ห้ามลง localStorage (03 §1.6.1)
 *   ถ้าเก็บใน localStorage สคริปต์ใด ๆ ที่หลุดเข้ามาในหน้าจะอ่านได้
 *   ส่วน refresh token อยู่ใน httpOnly cookie ซึ่ง JavaScript แตะไม่ได้เลย
 *   → รีเฟรชหน้าแล้วเรียก `/auth/refresh` เพื่อได้ access token ใหม่
 *
 * ⚠️ PDPA — ห้ามเก็บชื่อเล่นเด็กลง localStorage เช่นกัน
 *   สิ่งเดียวที่เก็บได้คือ `org_code` (ผู้ปกครองเห็นในหน้าตั้งค่าอยู่แล้ว)
 *   เพื่อไม่ให้เด็กต้องพิมพ์รหัสครอบครัวใหม่ทุกครั้ง
 */
import { create } from "zustand";

import { api, setAccessToken } from "./api";
import type { AuthTokenResponse, MeResponse } from "./types";

const ORG_CODE_KEY = "tewtorp.org_code";

export function rememberedOrgCode(): string {
  try {
    return localStorage.getItem(ORG_CODE_KEY) ?? "";
  } catch {
    // โหมดส่วนตัวของ Safari โยน error ตอนแตะ localStorage — ไม่ใช่เหตุให้แอปพัง
    return "";
  }
}

export function rememberOrgCode(code: string): void {
  try {
    localStorage.setItem(ORG_CODE_KEY, code);
  } catch {
    // เขียนไม่ได้ก็แค่ต้องพิมพ์ใหม่ครั้งหน้า
  }
}

/** รหัสที่จำไว้ใช้ไม่ได้แล้ว (ผู้ปกครองเปลี่ยนรหัส — D16) — ลืมทิ้ง ไม่งั้นจะเสนอรหัสตายให้เด็กทุกครั้ง */
export function forgetOrgCode(): void {
  try {
    localStorage.removeItem(ORG_CODE_KEY);
  } catch {
    // อ่าน/เขียนไม่ได้อยู่แล้ว ไม่มีอะไรให้ลืม
  }
}

interface SessionState {
  ready: boolean;
  me: MeResponse | null;
  role: string | null;
  learnerId: string | null;
  /** true ระหว่างรอผลการต่ออายุตอนเปิดแอปครั้งแรก */
  bootstrap: () => Promise<void>;
  adopt: (tokens: AuthTokenResponse) => Promise<void>;
  reload: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useSession = create<SessionState>((set) => ({
  ready: false,
  me: null,
  role: null,
  learnerId: null,

  async bootstrap() {
    // เปิดแอปครั้งแรก / รีเฟรชหน้า — ลองต่ออายุจาก cookie ก่อน
    const ok = await api.refresh();
    if (!ok) {
      set({ ready: true, me: null, role: null, learnerId: null });
      return;
    }
    try {
      const me = await api.get<MeResponse>("/auth/me");
      set({
        ready: true,
        me,
        role: me.user.role,
        learnerId: me.active_learner?.id ?? null,
      });
    } catch {
      set({ ready: true, me: null, role: null, learnerId: null });
    }
  },

  async adopt(tokens) {
    setAccessToken(tokens.access_token);
    const me = await api.get<MeResponse>("/auth/me");
    set({ ready: true, me, role: tokens.role, learnerId: tokens.learner_id });
  },

  async reload() {
    const me = await api.get<MeResponse>("/auth/me");
    set({ me, role: me.user.role, learnerId: me.active_learner?.id ?? null });
  },

  async logout() {
    try {
      await api.post("/auth/logout");
    } catch {
      // ออกจากระบบต้องสำเร็จเสมอในสายตาผู้ใช้ แม้เซิร์ฟเวอร์จะตอบไม่ได้
    }
    setAccessToken(null);
    set({ ready: true, me: null, role: null, learnerId: null });
  },
}));
